import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../layouts/Header';
import Sidebar from '../layouts/Sidebar';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import { pdf } from '@react-pdf/renderer';
import ExportPDFStandardEmploymentContract from './ExportPDFStandardEmploymentContract';

// eslint-disable-next-line react/prop-types
const StandardEmploymentContract = ({idBiodata, booking_id}) => {
  const [formData, setFormData] = useState({
    // Section A: Particulars of Parties
    employerName: '',
    employerIc: '',
    employerAddress: '',
    fdwName: '',
    fdwWp: '',
    fdwPassport: '',
    
    // Section B: Terms of Contract
    employmentPeriod: '',
    fdwResponsibilities: '',
    salary: '',
    restComp: '',
    salaryDate: '',
    payMethod: '',
    
    // Responsibilities of the Employer
    meals: false,
    rest: false,
    accommodation: '',
    employerResp: '',
    
    // Rest Days
    dailyWage: '',
    
    // Contract clauses
    termination: '',
    extension: '',
    others: '',
    
    // Section C: Employer's Declaration
    employerSignature: '',
    employerDate: '',
    eaWitnessEmp: '',
    eaRegEmp: '',
    
    // Section D: FDW's Declaration
    fdwSignature: '',
    fdwDate: '',
    eaWitnessFdw: '',
    eaRegFdw: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Fetch data for the booking_id
    fetchContractData();
  }, [booking_id]);

  const [agencyInfo, setAgencyInfo] = useState({});
  const fetchContractData = async () => {
    if (!booking_id) {
      console.log('No booking_id provided, keeping form initialized');
      return;
    }

    setIsLoading(true);
    try {
      console.log('Fetching Standard Employment Contract data for booking_id:', booking_id);
      const response = await api.get(`external-forms/standard-employment-contracts/${booking_id}/`);
      
      if (response.data) {
        console.log('Standard Employment Contract data retrieved:', response.data);
        
        // Map backend field names to frontend form fields
        const backendData = response.data;
        setAgencyInfo({
          agencyAddress: backendData.agency_address || '',
          agencyPhone: backendData.agency_phone || ''
        });
        setFormData({
          employerName: backendData.employer_name || '',
          employerIc: backendData.employer_ic || '',
          employerAddress: backendData.employer_address || '',
          fdwName: backendData.fdw_name || '',
          fdwWp: backendData.fdw_wp || '',
          fdwPassport: backendData.fdw_passport || '',
          employmentPeriod: backendData.employment_period || '',
          fdwResponsibilities: backendData.fdw_responsibilities || '',
          salary: backendData.salary?.toString() || '',
          restComp: backendData.rest_comp?.toString() || '',
          salaryDate: backendData.salary_date || '',
          payMethod: backendData.pay_method || '',
          meals: backendData.meals || false,
          rest: backendData.rest || false,
          accommodation: backendData.accommodation || '',
          employerResp: backendData.employer_resp || '',
          dailyWage: backendData.daily_wage?.toString() || '',
          termination: backendData.termination || '',
          extension: backendData.extension || '',
          others: backendData.others || '',
          employerSignature: backendData.employer_signature || '',
          employerDate: backendData.employer_date || '',
          eaWitnessEmp: backendData.ea_witness_emp || '',
          eaRegEmp: backendData.ea_reg_emp || '',
          fdwSignature: backendData.fdw_signature || '',
          fdwDate: backendData.fdw_date || '',
          eaWitnessFdw: backendData.ea_witness_fdw || '',
          eaRegFdw: backendData.ea_reg_fdw || ''
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        console.log('No existing Standard Employment Contract found for booking_id:', booking_id, '- keeping form initialized');
      } else {
        console.error('Error fetching Standard Employment Contract data:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-calculate daily wage when salary changes
  useEffect(() => {
    if (formData.salary) {
      const dailyWage = (parseFloat(formData.salary) / 26).toFixed(2);
      setFormData(prev => ({
        ...prev,
        dailyWage: dailyWage
      }));
    }
  }, [formData.salary]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'meals' || name === 'rest') {
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      } else if (name === 'accom') {
        setFormData(prev => ({
          ...prev,
          accommodation: checked ? value : ''
        }));
      }
    } else if (type === 'radio') {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    // All fields are optional - no required field validations
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare data for API submission, mapping frontend fields to backend fields
      const apiData = {
        booking: booking_id,
        employer_name: formData.employerName,
        employer_ic: formData.employerIc,
        employer_address: formData.employerAddress,
        fdw_name: formData.fdwName,
        fdw_wp: formData.fdwWp,
        fdw_passport: formData.fdwPassport,
        employment_period: formData.employmentPeriod,
        fdw_responsibilities: formData.fdwResponsibilities,
        salary: parseFloat(formData.salary) || null,
        rest_comp: parseFloat(formData.restComp) || null,
        salary_date: formData.salaryDate,
        pay_method: formData.payMethod,
        meals: formData.meals,
        rest: formData.rest,
        accommodation: formData.accommodation,
        employer_resp: formData.employerResp,
        daily_wage: parseFloat(formData.dailyWage) || null,
        termination: formData.termination,
        extension: formData.extension,
        others: formData.others,
        employer_signature: formData.employerSignature,
        employer_date: formData.employerDate || null,
        ea_witness_emp: formData.eaWitnessEmp,
        ea_reg_emp: formData.eaRegEmp,
        fdw_signature: formData.fdwSignature,
        fdw_date: formData.fdwDate || null,
        ea_witness_fdw: formData.eaWitnessFdw,
        ea_reg_fdw: formData.eaRegFdw
      };

      console.log('Submitting Standard Employment Contract Data:', apiData);
      
      const response = await api.post(`external-forms/standard-employment-contracts/${booking_id}/`, apiData);
      console.log('Standard Employment Contract submitted successfully:', response.data);
      
      // Show success message
      alert('Standard Employment Contract submitted successfully!');
      
      // Clear any errors
      setErrors({});
      
    } catch (error) {
      console.error('Error submitting Standard Employment Contract:', error);
      
      if (error.response && error.response.data) {
        console.error('Error details:', error.response.data);
        
        // Handle field-specific errors from backend
        if (typeof error.response.data === 'object') {
          setErrors(error.response.data);
        } else {
          alert(`Error submitting form: ${JSON.stringify(error.response.data)}`);
        }
      } else {
        alert('Error submitting form. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      employerName: '',
      employerId: '',
      employerAddress: '',
      fdwName: '',
      fdwWp: '',
      fdwPassport: '',
      employmentPeriod: '',
      fdwResponsibilities: '',
      salary: '',
      restComp: '',
      salaryDate: '',
      payMethod: '',
      meals: false,
      rest: false,
      accommodation: '',
      employerResp: '',
      dailyWage: '',
      termination: '',
      extension: '',
      others: '',
      employerSignature: '',
      employerDate: '',
      eaWitnessEmp: '',
      eaRegEmp: '',
      fdwSignature: '',
      fdwDate: '',
      eaWitnessFdw: '',
      eaRegFdw: ''
    });
  };

  const handleDelete = async () => {
    if (!booking_id) {
      alert('No booking ID provided for deletion');
      return;
    }

    const confirmDelete = window.confirm(
      'Are you sure you want to delete this Standard Employment Contract record? This action cannot be undone.'
    );

    if (!confirmDelete) {
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('Deleting Standard Employment Contract for booking_id:', booking_id);
      const response = await api.delete(`external-forms/standard-employment-contracts/${booking_id}/`);
      console.log('Standard Employment Contract deleted successfully:', response.data);
      
      // Show success message
      alert('Standard Employment Contract record deleted successfully!');
      
      // Reset the form after deletion
      handleReset();
      
    } catch (error) {
      console.error('Error deleting Standard Employment Contract:', error);
      alert('Standard Employment Contract record deleted successfully!');
    } finally {
      setIsSubmitting(false);
    }
  };

  const { user } = useAuth();
  const handleDownloadPDF = async () => {
    setIsSubmitting(true);

    try {
      console.log('Generating Standard Employment Contract PDF for booking_id:', booking_id);
      
      // Prepare the data for PDF generation
      const pdfData = {
        ...formData,
        booking_id: booking_id
      };

      // Generate PDF using React PDF
      const blob = await pdf(
        <ExportPDFStandardEmploymentContract
          user={user}
          data={pdfData}
          agencyInfo={agencyInfo}
        />
      ).toBlob();
      
      // Create blob link to download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with current date
      const today = new Date().toISOString().split('T')[0];
      const filename = booking_id 
        ? `Standard_Employment_Contract_${booking_id}_${today}.pdf`
        : `Standard_Employment_Contract_${today}.pdf`;
      link.setAttribute('download', filename);
      
      // Append to html link element page
      document.body.appendChild(link);
      
      // Start download
      link.click();
      
      // Clean up and remove the link
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('Standard Employment Contract PDF generated and downloaded successfully');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      
      <div className="page-wrapper" style={{ marginLeft: '0' }}>
        <div className="content container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="content-page-header">
                    <h5>Standard Employment Contract</h5>
                  </div>

                  {/* Loading Indicator */}
                  {isLoading && (
                    <div className="d-flex justify-content-center align-items-center py-5">
                      <div className="spinner-border text-primary me-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span className="text-muted">Loading existing contract data...</span>
                    </div>
                  )}

                  {/* Form Content - Hidden during loading */}
                  <div style={{ display: isLoading ? 'none' : 'block' }}>
                  <form onSubmit={handleSubmit}>
                    {/* Section A: Particulars of Parties */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Section A: Particulars of Parties</h6>
                      </div>
                      <div className="card-body">
                        <h6 className="mb-3">Employer</h6>
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Full Name</label>
                              <input
                                type="text"
                                name="employerName"
                                value={formData.employerName}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>NRIC / Passport No</label>
                              <input
                                type="text"
                                name="employerIc"
                                value={formData.employerIc}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="input-block mb-4">
                          <label>Address</label>
                          <input
                            type="text"
                            name="employerAddress"
                            value={formData.employerAddress}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                        </div>

                        <h6 className="mb-3">Foreign Domestic Worker (FDW)</h6>
                        <div className="row">
                          <div className="col-lg-4 col-12">
                            <div className="input-block mb-3">
                              <label>Full Name</label>
                              <input
                                type="text"
                                name="fdwName"
                                value={formData.fdwName}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-4 col-12">
                            <div className="input-block mb-3">
                              <label>Work Permit No</label>
                              <input
                                type="text"
                                name="fdwWp"
                                value={formData.fdwWp}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-4 col-12">
                            <div className="input-block mb-3">
                              <label>Passport No</label>
                              <input
                                type="text"
                                name="fdwPassport"
                                value={formData.fdwPassport}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Section B: Terms of Contract */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Section B: Terms of Contract</h6>
                      </div>
                      <div className="card-body">
                        <h6 className="mb-3">Employment Period & Work Place</h6>
                        <div className="input-block mb-4">
                          <textarea
                            name="employmentPeriod"
                            value={formData.employmentPeriod}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="3"
                            placeholder="FDW employed for 2 years at employer's residence"
                          />
                        </div>

                        <h6 className="mb-3">Responsibilities of the FDW</h6>
                        <div className="input-block mb-4">
                          <textarea
                            name="fdwResponsibilities"
                            value={formData.fdwResponsibilities}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="3"
                            placeholder="Diligent, honest, no other jobs, personal effects, follow Annex A"
                          />
                        </div>

                        <h6 className="mb-3">Remuneration & Benefits</h6>
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Monthly Salary (S$)</label>
                              <input
                                type="number"
                                name="salary"
                                value={formData.salary}
                                onChange={handleInputChange}
                                className="form-control"
                                step="0.01"
                                min="0"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Rest Day Compensation (S$)</label>
                              <input
                                type="number"
                                name="restComp"
                                value={formData.restComp}
                                onChange={handleInputChange}
                                className="form-control"
                                step="0.01"
                                min="0"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="input-block mb-3">
                          <label>Salary Payment Date</label>
                          <input
                            type="text"
                            name="salaryDate"
                            value={formData.salaryDate}
                            onChange={handleInputChange}
                            className="form-control"
                            placeholder="e.g. First day of month"
                          />
                        </div>

                        <label className="mb-2">Payment Method</label>
                        <div className="row">
                          {['Cash', 'Bank transfer'].map((method) => (
                            <div key={method} className="col-lg-6 col-12">
                              <div className="form-check mb-3">
                                <input
                                  type="radio"
                                  name="payMethod"
                                  value={method}
                                  checked={formData.payMethod === method}
                                  onChange={handleInputChange}
                                  className="form-check-input"
                                  id={`pay-${method}`}
                                />
                                <label className="form-check-label" htmlFor={`pay-${method}`}>
                                  {method}
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>

                        <h6 className="mb-3">Responsibilities of the Employer</h6>
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="form-check mb-2">
                              <input
                                type="checkbox"
                                name="meals"
                                checked={formData.meals}
                                onChange={handleInputChange}
                                className="form-check-input"
                                id="meals"
                              />
                              <label className="form-check-label" htmlFor="meals">
                                Provide 3 meals daily
                              </label>
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="form-check mb-2">
                              <input
                                type="checkbox"
                                name="rest"
                                checked={formData.rest}
                                onChange={handleInputChange}
                                className="form-check-input"
                                id="rest"
                              />
                              <label className="form-check-label" htmlFor="rest">
                                8 hours nightly rest
                              </label>
                            </div>
                          </div>
                        </div>

                        <label className="mb-2">Accommodation</label>
                        <div className="row">
                          {[
                            { value: 'Separate room', label: 'Separate room' },
                            { value: 'Shared room', label: 'Share room with child/care recipient' },
                            { value: 'Other', label: 'Other accommodation' }
                          ].map((option) => (
                            <div key={option.value} className="col-lg-4 col-12">
                              <div className="form-check mb-2">
                                <input
                                  type="radio"
                                  name="accom"
                                  value={option.value}
                                  checked={formData.accommodation === option.value}
                                  onChange={handleInputChange}
                                  className="form-check-input"
                                  id={`accom-${option.value}`}
                                />
                                <label className="form-check-label" htmlFor={`accom-${option.value}`}>
                                  {option.label}
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="input-block mb-3 mt-3">
                          <textarea
                            name="employerResp"
                            value={formData.employerResp}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="3"
                            placeholder="Safe conditions, no abuse, medical treatment, insurance, repatriation, communications"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Rest Days Section */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Rest Days</h6>
                      </div>
                      <div className="card-body">
                        <p className="text-muted small mb-3">
                          As per MOM guidelines: 4 rest days per month. Compensation in lieu must be at least daily wage.
                        </p>
                        
                        <div className="input-block mb-3">
                          <label>Daily Wage (auto-calculated from salary ÷ 26)</label>
                          <input
                            type="text"
                            name="dailyWage"
                            value={formData.dailyWage}
                            className="form-control"
                            readOnly
                            placeholder="Will auto-calculate when salary is entered"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Contract Clauses */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Contract Clauses</h6>
                      </div>
                      <div className="card-body">
                        <div className="input-block mb-3">
                          <label>Termination of Contract</label>
                          <textarea
                            name="termination"
                            value={formData.termination}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="3"
                            placeholder="30 days notice, immediate for breach, employer responsible for repatriation"
                          />
                        </div>

                        <div className="input-block mb-3">
                          <label>Extension of Contract</label>
                          <textarea
                            name="extension"
                            value={formData.extension}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="3"
                            placeholder="15 days paid home leave with round trip ticket, or lump sum compensation"
                          />
                        </div>

                        <div className="input-block mb-3">
                          <label>Others</label>
                          <textarea
                            name="others"
                            value={formData.others}
                            onChange={handleInputChange}
                            className="form-control"
                            rows="3"
                            placeholder="English version prevails, disputes via mediation, laws of Singapore apply"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Section C: Employer's Declaration */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Section C: Employer&apos;s Declaration</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Employer&apos;s Signature</label>
                              <input
                                type="text"
                                name="employerSignature"
                                value={formData.employerSignature}
                                onChange={handleInputChange}
                                className="form-control"
                                placeholder="Type name as signature"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Date</label>
                              <input
                                type="date"
                                name="employerDate"
                                value={formData.employerDate}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Witnessed by EA Personnel (Name)</label>
                              <input
                                type="text"
                                name="eaWitnessEmp"
                                value={formData.eaWitnessEmp}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Registration</label>
                              <input
                                type="text"
                                name="eaRegEmp"
                                value={formData.eaRegEmp}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Section D: FDW's Declaration */}
                    <div className="card mb-4">
                      <div className="card-header">
                        <h6 className="mb-0">Section D: FDW&apos;s Declaration</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Signature of FDW</label>
                              <input
                                type="text"
                                name="fdwSignature"
                                value={formData.fdwSignature}
                                onChange={handleInputChange}
                                className="form-control"
                                placeholder="Type name as signature"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Date</label>
                              <input
                                type="date"
                                name="fdwDate"
                                value={formData.fdwDate}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Witnessed by EA Personnel (Name)</label>
                              <input
                                type="text"
                                name="eaWitnessFdw"
                                value={formData.eaWitnessFdw}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                          <div className="col-lg-6 col-12">
                            <div className="input-block mb-3">
                              <label>Registration</label>
                              <input
                                type="text"
                                name="eaRegFdw"
                                value={formData.eaRegFdw}
                                onChange={handleInputChange}
                                className="form-control"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Form Buttons */}
                    <div className="row">
                      <div className="col-12">
                        <div className="btn-path text-end">
                          <button
                            type="button"
                            onClick={handleReset}
                            className="btn btn-cancel bg-primary-light me-3"
                            disabled={isSubmitting}
                          >
                            Reset
                          </button>
                          <button
                            type="button"
                            onClick={handleDelete}
                            className="btn btn-danger me-3"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? 'Deleting...' : 'Clean'}
                          </button>
                          <button
                            type="button"
                            onClick={handleDownloadPDF}
                            className="btn btn-success me-3"
                            disabled={isSubmitting}
                            title="Download PDF of current form data"
                          >
                            {isSubmitting ? 'Generating...' : (
                              <>
                                <i className="fas fa-download me-2"></i>
                                Download PDF
                              </>
                            )}
                          </button>
                          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                             {isSubmitting ? 'Submitting...' : 'Save / Submit'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StandardEmploymentContract;